import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCountries } from "../../context/CountryContext";

import api from "../../config/api";
import logo from "../../assets/waseeny-logo.png";
import PhoneInput from "../Common/PhoneInput";
import {
  DEFAULT_PHONE_COUNTRY,
  buildPhonePayload,
  isPhoneComplete,
  formatPhonePreview,
} from "../../utils/phone";

export default function AddMobile({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { countries } = useCountries();

  const pendingGoogleCredential = sessionStorage.getItem("pendingGoogleCredential");
  const isGoogleFlow = Boolean(pendingGoogleCredential);
  const [step, setStep] = useState("mobile"); // mobile, otp
  const [phone, setPhone] = useState({
    countryCode: DEFAULT_PHONE_COUNTRY,
    nationalNumber: "",
  });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleInitiate = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setMessage("");

    if (!isPhoneComplete(phone, countries)) {
      setError("Please enter a valid mobile number");
      return;
    }

    setLoading(true);
    try {
      if (isGoogleFlow) {
        const res = await api.post("/auth/google/mobile/initiate", {
          token: pendingGoogleCredential,
          ...buildPhonePayload(phone),
        });
        setMessage(res.data.message);
        setStep("otp");
        setTimer(120);
        return;
      }

      const res = await api.post("/auth/add-mobile/initiate", buildPhonePayload(phone));
      setMessage(res.data.message);
      setStep("otp");
      setTimer(120); // 2 minutes
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (otp.length !== 6) {
      setError("Please enter 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const redirectTo = location.state?.from || sessionStorage.getItem("pendingGoogleRedirect") || "/home";

      if (pendingGoogleCredential) {
        const googleRes = await api.post("/auth/google/mobile/verify", {
          token: pendingGoogleCredential,
          ...buildPhonePayload(phone),
          otp,
        });

        if (!googleRes.data?.token) {
          throw new Error("Google login token missing");
        }

        localStorage.setItem("token", googleRes.data.token);
        sessionStorage.removeItem("pendingGoogleCredential");
        sessionStorage.removeItem("pendingGoogleRedirect");
      } else {
        await api.post("/auth/add-mobile/verify", {
          ...buildPhonePayload(phone),
          otp,
        });
      }

      setIsAuthenticated(true);

      // Redirect back to original destination or home
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "radial-gradient(ellipse 90% 90% at 50% 48%, rgba(255,253,248,0.86) 0%, rgba(255,251,244,0.76) 38%, rgba(255,248,238,0.50) 70%, rgba(255,245,230,0.22) 100%)",
      fontFamily: "'DM Sans', sans-serif",
      padding: "20px",
      position: "relative",
    },
    backBtn: {
      position: "absolute",
      top: "30px",
      left: "30px",
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "50%",
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 10,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    },
    card: {
      width: "100%",
      maxWidth: "450px",
      padding: "40px",
      background: "#fff",
      borderRadius: "24px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
      textAlign: "center",
    },
    logo: { height: "80px", marginBottom: "20px" },
    title: { fontSize: "28px", fontWeight: "800", color: "#111", marginBottom: "8px" },
    subtitle: { fontSize: "15px", color: "#666", marginBottom: "30px" },
    input: {
      width: "100%",
      padding: "16px",
      fontSize: "16px",
      borderRadius: "12px",
      border: "1.5px solid #e2e8f0",
      outline: "none",
      marginBottom: "20px",
      boxSizing: "border-box",
    },
    button: {
      width: "100%",
      padding: "16px",
      background: "linear-gradient(135deg, #E31E24 0%, #C8181E 100%)",
      color: "#fff",
      fontSize: "16px",
      fontWeight: "700",
      border: "none",
      borderRadius: "50px",
      cursor: "pointer",
      boxShadow: "0 4px 18px rgba(227,30,36,0.3)",
    },
    alert: {
      padding: "12px",
      borderRadius: "8px",
      fontSize: "14px",
      marginBottom: "20px",
    },
    error: { background: "#FEE2E2", color: "#C53030" },
    success: { background: "#DEF7EC", color: "#046C4E" },
    resend: {
      background: "none",
      border: "none",
      color: "#E31E24",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "15px",
    }
  };

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.backBtn}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div style={styles.card}>
        <img src={logo} alt="Logo" style={styles.logo} />
        <h1 style={styles.title}>
          {isGoogleFlow ? "Add Mobile Number" : (step === "mobile" ? "One Last Step" : "Verify Number")}
        </h1>
        <p style={styles.subtitle}>
          {isGoogleFlow
            ? "Please enter your mobile number to complete Google sign in."
            : step === "mobile" 
            ? "Please provide your mobile number to continue." 
            : `Enter the code sent to ${formatPhonePreview(phone, countries)}`}
        </p>

        {error && <div style={{ ...styles.alert, ...styles.error }}>{error}</div>}
        {message && !isGoogleFlow && <div style={{ ...styles.alert, ...styles.success }}>{message}</div>}

        {isGoogleFlow || step === "mobile" ? (
          <form onSubmit={handleInitiate}>
            <PhoneInput
              label=""
              value={phone}
              onChange={(nextPhone) => {
                setPhone(nextPhone);
                setError("");
              }}
              disabled={loading}
              required
              wrapperStyle={{ marginBottom: "20px" }}
              inputStyle={styles.input}
              selectStyle={styles.input}
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? (isGoogleFlow ? "Continuing..." : "Sending...") : (isGoogleFlow ? "Continue" : "Send OTP")}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <input
              type="text"
              style={{ ...styles.input, textAlign: "center", letterSpacing: "8px" }}
              placeholder="000000"
              maxLength="6"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              required
            />
            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <div style={{ marginTop: "20px", fontSize: "14px" }}>
              {timer > 0 ? (
                `Resend OTP in ${timer}s`
              ) : (
                <button 
                  type="button" 
                  style={styles.resend} 
                  onClick={() => handleInitiate()}
                  disabled={loading}
                >
                  Resend Code
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
